import { useRef, useState, type ReactNode } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<unknown>;
  /** Pixels to pull before triggering a refresh. */
  threshold?: number;
  children: ReactNode;
}

/**
 * Lightweight pull-to-refresh. Listens to touch events only (mouse is
 * intentionally untouched — desktop users refresh by re-navigating).
 *
 * Triggers `onRefresh()` when the user pulls past `threshold` px while
 * the scroll container is at the top. Visual spinner shows at the top
 * and retracts with a short animation.
 */
export function PullToRefresh({ onRefresh, threshold = 70, children }: PullToRefreshProps) {
  const [pulling, setPulling] = useState(false);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  function onTouchStart(e: React.TouchEvent) {
    // Find nearest scroll container (content-scroll in the layout)
    const el = (e.target as HTMLElement).closest('.content-scroll') as HTMLDivElement | null;
    scrollContainerRef.current = el;
    if (el && el.scrollTop <= 0 && e.touches[0]) {
      startY.current = e.touches[0].clientY;
    } else {
      startY.current = null;
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (startY.current === null) return;
    const el = scrollContainerRef.current;
    if (!el || el.scrollTop > 0) {
      startY.current = null;
      setOffset(0);
      setPulling(false);
      return;
    }
    const delta = (e.touches[0]?.clientY ?? startY.current) - startY.current;
    if (delta > 0) {
      // dampen: the further you pull, the slower it moves (feels natural)
      const damped = Math.min(threshold * 1.5, delta * 0.5);
      setOffset(damped);
      setPulling(true);
    }
  }

  async function onTouchEnd() {
    if (!pulling) return;
    const triggered = offset >= threshold && !refreshing;
    setPulling(false);
    if (triggered) {
      setRefreshing(true);
      setOffset(threshold);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setOffset(0);
      }
    } else {
      setOffset(0);
    }
    startY.current = null;
  }

  const showSpinner = pulling || refreshing;

  return (
    <div
      className="pull-to-refresh"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="ptr-indicator"
        data-active={showSpinner || undefined}
        data-refreshing={refreshing || undefined}
        style={{ height: offset }}
      >
        <span className="material-symbols-outlined" style={{ transform: `rotate(${Math.min(360, offset * 4)}deg)` }}>
          {refreshing ? 'sync' : 'arrow_downward'}
        </span>
      </div>
      <div
        className="ptr-content"
        style={{
          transform: offset ? `translateY(${offset}px)` : undefined,
          transition: pulling ? 'none' : 'transform 220ms ease',
        }}
      >
        {children}
      </div>
    </div>
  );
}
